/**
 * Sorprendeme - Dynamic Backgrounds Helper
 * Maneja la selección aleatoria de backgrounds desde variable de entorno
 * 
 * IMPORTANT: Background URLs MUST be configured via Railway environment variable
 * VITE_SORPRENDEME_BACKGROUNDS (comma-separated URLs)
 * 
 * SUPPORTED FORMATS:
 * 
 * 1. Cloudflare Images (RECOMMENDED for LATAM):
 *    - Basic: https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/IMAGE_ID/public
 *    - Optimized: https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/IMAGE_ID/w=1200,q=75,format=auto
 *    - The URL does NOT end in .webp - Cloudflare converts automatically
 *    - format=auto → serves AVIF/WebP/JPEG based on browser support
 * 
 * 2. Unsplash:
 *    - https://images.unsplash.com/photo-XXXXXX?w=1200&q=75&fm=webp&fit=crop
 * 
 * 3. Direct CDN URLs:
 *    - https://cdn.example.com/image.webp
 *    - https://cdn.example.com/image.jpg
 *    - https://cdn.example.com/image.png
 * 
 * Performance optimizations:
 * - Recommended dimensions: 1200px (mobile-first)
 * - Recommended quality: 75% (imperceptible loss, smaller file)
 * - Memoized parsing to avoid repeated operations
 */

const isDev = import.meta.env.DEV;

// Fallback background (single solid color gradient) if env var not configured
// This ensures the page always renders, but backgrounds MUST be configured in production
const FALLBACK_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

// Cache parsed URLs to avoid repeated parsing (performance optimization)
let cachedBackgrounds: string[] | null = null;

/**
 * Parse background URLs from environment variable with caching.
 * Format: URLs separated by comma. Supports any public URL:
 * - Cloudflare Images: https://imagedelivery.net/<account>/<image-id>/w=1200,q=75,format=auto
 * - Unsplash: https://images.unsplash.com/photo-XXXXXX?w=1200&q=75&fm=webp&fit=crop
 * - Any CDN with optimized image URLs
 * 
 * CRITICAL: If env var is not set, returns empty array (fallback to gradient)
 */
function parseBackgroundUrls(): string[] {
  // Return cached version if available (performance optimization)
  if (cachedBackgrounds !== null) {
    return cachedBackgrounds;
  }

  const envValue = import.meta.env.VITE_SORPRENDEME_BACKGROUNDS;
  
  if (isDev) {
    console.log('[sorprende-backgrounds] Parsing backgrounds from env');
  }
  
  if (!envValue || typeof envValue !== 'string' || envValue.trim().length === 0) {
    console.warn('[sorprende-backgrounds] ⚠️ VITE_SORPRENDEME_BACKGROUNDS not configured!');
    console.warn('[sorprende-backgrounds] Configure in Railway: juaperrone-main service → Variables');
    console.warn('[sorprende-backgrounds] Format: URL1,URL2,URL3 (comma-separated)');
    console.warn('[sorprende-backgrounds] Falling back to gradient background');
    
    cachedBackgrounds = []; // Empty array = use gradient fallback
    return cachedBackgrounds;
  }
  
  // Split by comma and trim whitespace
  const urls = envValue
    .split(',')
    .map(url => url.trim())
    .filter(url => url.length > 0 && (url.startsWith('http://') || url.startsWith('https://')));
  
  // Validate URLs
  if (urls.length === 0) {
    console.warn('[sorprende-backgrounds] ⚠️ No valid URLs found in VITE_SORPRENDEME_BACKGROUNDS');
    console.warn('[sorprende-backgrounds] Falling back to gradient background');
    cachedBackgrounds = [];
    return cachedBackgrounds;
  }
  
  if (isDev) {
    console.log('[sorprende-backgrounds] ✓ Loaded', urls.length, 'background(s) from env');
  }
  
  cachedBackgrounds = urls;
  return cachedBackgrounds;
}

/**
 * Get a random background URL from Railway environment variable
 * Uses session storage to persist the same background during user session
 * 
 * Returns:
 * - Image URL if VITE_SORPRENDEME_BACKGROUNDS is configured (use with url())
 * - null if not configured (component will use gradient directly)
 * 
 * IMPORTANT: VITE_SORPRENDEME_BACKGROUNDS MUST be set in Railway
 */
export function getRandomBackground(): string | null {
  // Get backgrounds from env var
  const backgrounds = parseBackgroundUrls();
  
  // If no backgrounds configured, return null (component will use gradient)
  if (backgrounds.length === 0) {
    console.warn('[sorprende-backgrounds] No backgrounds configured - component will use gradient');
    console.warn('[sorprende-backgrounds] Configure VITE_SORPRENDEME_BACKGROUNDS in Railway');
    return null;
  }
  
  // Select random background (rotates on each page load)
  const randomIndex = Math.floor(Math.random() * backgrounds.length);
  const selectedBg = backgrounds[randomIndex];
  
  if (isDev) {
    console.log('[sorprende-backgrounds] Selected background #' + (randomIndex + 1) + '/' + backgrounds.length + ':', selectedBg);
  }
  
  return selectedBg;
}

/**
 * Get all available backgrounds (for testing/preview)
 */
export function getAllBackgrounds(): string[] {
  return parseBackgroundUrls();
}

/**
 * Clear session storage (no longer needed - backgrounds rotate on each visit)
 * @deprecated Session storage removed to enable background rotation
 */
export function clearBackgroundSession(): void {
  // No-op: session storage no longer used
}
